// src/Dashboard/pages/teacher/AttendanceTable/components/AttendanceGroupPickerPanel.jsx

import React, { useMemo, useState } from "react";
import { FaLayerGroup, FaSearch, FaUsers } from "react-icons/fa";
import { isActiveGroup } from "../utils/attendanceTableUtils";

function getDojoName(group) {
  return group?.dojo_nom || group?.Dojo?.dojo_nom || group?.dojo?.dojo_nom || "";
}

export default function AttendanceGroupPickerPanel({
  groups = [],
  loading,
  selectedGroupId,
  onSelectGroup,
}) {
  const [search, setSearch] = useState("");

  const filteredGroups = useMemo(() => {
    const query = search.trim().toLowerCase();

    return (groups || [])
      .filter(isActiveGroup)
      .filter((group) => {
        if (!query) return true;

        return [group?.nom, getDojoName(group)]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(query);
      })
      .slice()
      .sort((a, b) =>
        String(a?.nom || "").localeCompare(String(b?.nom || ""), "fr")
      );
  }, [groups, search]);

  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="p-4 sm:p-5 lg:p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              Étape 1
            </p>

            <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
              Choisir le groupe à consulter
            </h2>

            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
              Sur smartphone, l’enseignant choisit directement le groupe en
              plein écran, sans passer par une liste déroulante.
            </p>
          </div>

          <div className="relative w-full lg:max-w-md">
            <FaSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />

            <input
              type="text"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Rechercher un groupe..."
              className="w-full rounded-2xl border border-slate-200 bg-slate-50 py-3 pl-11 pr-4 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
            />
          </div>
        </div>

        {loading ? (
          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {[...Array(8)].map((_, index) => (
              <div
                key={index}
                className="h-28 animate-pulse rounded-[24px] border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-800/60"
              />
            ))}
          </div>
        ) : filteredGroups.length === 0 ? (
          <div className="mt-5 rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-10 text-center text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-800/40 dark:text-slate-400">
            Aucun groupe actif trouvé.
          </div>
        ) : (
          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {filteredGroups.map((group) => {
              const selected = String(selectedGroupId) === String(group.id);
              const dojoName = getDojoName(group);

              return (
                <button
                  key={group.id}
                  type="button"
                  onClick={() => onSelectGroup?.(String(group.id))}
                  className={`group rounded-[24px] border p-4 text-left transition hover:-translate-y-0.5 hover:shadow-md ${
                    selected
                      ? "border-indigo-300 bg-indigo-50 ring-2 ring-indigo-200 dark:border-indigo-500/30 dark:bg-indigo-500/10 dark:ring-indigo-500/20"
                      : "border-slate-200 bg-white hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:hover:bg-slate-900"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <span className="grid h-12 w-12 place-items-center rounded-2xl bg-indigo-50 text-indigo-700 ring-1 ring-indigo-100 dark:bg-indigo-500/10 dark:text-indigo-300 dark:ring-indigo-500/20">
                      <FaLayerGroup />
                    </span>

                    <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-1 text-[11px] font-bold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                      <FaUsers />
                      Groupe
                    </span>
                  </div>

                  <h3 className="mt-4 line-clamp-2 text-lg font-bold text-slate-900 dark:text-white">
                    {group.nom || "Groupe"}
                  </h3>

                  {dojoName && (
                    <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                      Dojo : {dojoName}
                    </p>
                  )}

                  <div className="mt-4 rounded-2xl bg-slate-50 px-3 py-2 text-xs font-bold text-slate-600 transition group-hover:bg-white dark:bg-slate-900 dark:text-slate-300 dark:group-hover:bg-slate-800">
                    Ouvrir le tableau de présences
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}